import { Box, Button, Text, Icon, VStack, IconButton, useDisclosure } from "@chakra-ui/react"
import { useUploadFiles } from "lib/hooks";
import { useEffect, useRef, useState, FC } from 'react';
import { DropFileInProgress } from "domains/drop-file/dropFileInProgress";
import { BiUpload } from '@react-icons/all-files/bi/BiUpload';
import { useDndAsset } from "domains/drag-and-drop";
import { DeleteIcon } from "@chakra-ui/icons";
import { DeleteAlertDialog } from "components";
import { usePackContext } from "domains/pack";

enum Events {
  dragenter = 'dragenter',
  dragleave = 'dragleave',
  dragover = 'dragover',
  drop = 'drop',
}

// TODO: Move to utils
const addEventListeners = (events: Events[], node: HTMLElement, cb: (e?: Event) => void) => {
  events.forEach((event) => {
    node.addEventListener(event, cb, false);
  })
}

// TODO: Move to utils
const removeEventListeners = (events: Events[], node: HTMLElement, cb: (e?: Event) => void) => {
  events.forEach((event) => {
    node.removeEventListener(event, cb, false);
  })
}

export const FilePrompt: FC<{ isEditable: boolean }> = ({ isEditable }) => {
  const { packId } = usePackContext();
  const { uploadFiles, percent } = useUploadFiles(packId);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const { asset } = useDndAsset();
  const deleteDialog = useDisclosure();
  const [isOver, setIsOver] = useState<boolean>(false);

  useEffect(() => {
    if (!ref.current) return;

    const node = ref.current;

    const onDragEnter = (event): void => {
      event.preventDefault();
      event.stopPropagation();

      if (!isOver) {
        setIsOver(true);
      }
    }

    const onDragLeave = (event): void => {
      if (isOver && (event.currentTarget === event.target || !event.currentTarget.contains(event.relatedTarget))) {
        setIsOver(false);
      }
    }

    const onDrop = async (event: DragEvent): Promise<void> => {
      event.preventDefault();
      event.stopPropagation();

      await uploadFiles(event.dataTransfer.files, { prompt_id: asset.id });
    }

    addEventListeners([Events.dragenter, Events.dragover], node, onDragEnter);
    addEventListeners([Events.dragleave], node, onDragLeave);
    addEventListeners([Events.drop], node, onDrop);

    return () => {
      removeEventListeners([Events.dragenter, Events.dragover], node, onDragEnter);
      removeEventListeners([Events.dragleave], node, onDragLeave);
      removeEventListeners([Events.drop], node, onDrop);
    }
  }, [isOver, uploadFiles]);

  const addFiles = async () => {
    await uploadFiles(inputRef.current.files, { prompt_id: asset.id });
  }

  const openFileDialog = () => {
    inputRef.current.click();
  }

  return (
    <Box
      ref={ref}
      position={"relative"}
      bgColor={ isOver ? 'brand.50' : 'transparent' }
      minH={"100px"}
      px={5}
      py={7}
      layerStyle={"with-transition"}
    >
      <VStack spacing={3} width={"100%"} mt={3}>
        { !percent ? (
          <>
            <Icon as={BiUpload} boxSize={10} color={"brand.500"} />
            <Text size={"sm"}>Drag &amp; drop or <Button variant={"link"} fontSize={"sm"} color="brand.500" fontWeight={400} onClick={openFileDialog}>upload a file</Button> from your device.</Text>
            <input
              ref={inputRef}
              id={`file-uploader-${asset.id}`}
              type="file"
              onChange={() => addFiles()}
              multiple
              style={{ display: 'none' }}
            />
            {isEditable ? (
              <IconButton
                position={"absolute"}
                top={0}
                right={3}
                width={["50%", "40px"]}
                icon={<DeleteIcon/>}
                aria-label={"Delete"}
                borderColor={"red"}
                onClick={deleteDialog.onOpen}
              />) : null
            }
          </>
        ) : <DropFileInProgress percent={percent} /> }
      </VStack>
      <DeleteAlertDialog
        title={"Are you sure you want to delete this file dropzone?"}
        content={"This action cannot be undone."}
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        asset={asset}
      />
    </Box>
  )
}